/**
 * Draft handoff between the `client_create_skill` client tool and the
 * Skills page's create-skill editor.
 *
 * The tool stashes a JSON draft under the storage key below and
 * navigates the user to ``/agents/skills``. The Skills page consumes the
 * draft once on mount, opens the create editor seeded with it, and clears
 * the slot — so refreshing the page or opening the Skills page from another
 * entry point produces the usual empty list (and an empty create form).
 *
 * The shape mirrors the editor's create-form fields rather than the wire
 * `CreateSkillRequest`: every field is optional so the LLM can emit what it
 * inferred and let the user fill or fix the rest in the editor, which owns
 * the final validation before the skill is persisted.
 *
 * SessionStorage is a trust boundary: values can be tampered with via
 * DevTools, browser extensions, or left behind by an older build with a
 * different schema. The consume path runs a runtime shape validator so the
 * editor's lazy state initializer never sees a malformed payload.
 */

import type { SkillAccessibility } from "./SkillRecord";

const SKILL_DRAFT_STORAGE_KEY = "roboto.skill-draft";

const ACCESSIBILITIES: readonly SkillAccessibility[] = [
  "private",
  "org",
  "org-editable",
];

/**
 * Partial skill specification handed from the client tool to the create
 * editor. Every field is optional; the editor maps missing fields back to
 * its own empty-form defaults and surfaces any remaining gaps via its own
 * validation.
 */
export interface SkillDraft {
  /** Slug-style name. May be invalid (e.g. contain spaces) — the editor's
   *  name field validates it and prompts the user to fix it. */
  name?: string;
  /** The "when to use" text. */
  description?: string;
  /** The procedure / body (Markdown). */
  body?: string;
  /** Visibility tier. Omitted leaves the editor on its `private` default. */
  accessibility?: SkillAccessibility;
  /** Tags to add. Merged with the skill's existing ones, never a replacement. */
  tags?: string[];
  /**
   * Tags to take off the skill. Update-only — a skill being created has none
   * to remove — and never overlapping {@link SkillDraft.tags}, which
   * `coerceToSkillDraft` guarantees by resolving a contradictory proposal in
   * favor of the addition.
   */
  removeTags?: string[];
  /**
   * Bare name of an EXISTING skill this draft targets for update rather than
   * create. When set, the Skills page resolves it against the caller's
   * editable skills and opens the skill's own page in edit mode with the
   * draft's `description` / `body` prefilled over the loaded version. Absent
   * for the create handoff.
   *
   * `coerceToSkillDraft` strips any registry disambiguation suffix (e.g.
   * `name(private)`) when it builds the draft, so the value stashed by the
   * tool is always the bare name. A payload that reaches
   * {@link parseSkillDraftShape} from anywhere else carries no such
   * guarantee; an unstripped name simply fails to resolve.
   */
  targetSkillName?: string;
}

/**
 * The subset of a {@link SkillDraft} that seeds an edit.
 *
 * `description` and `body` prefill the version's edit form. `tags` and
 * `removeTags` are different in kind — they belong to the skill rather than to
 * the version, and everywhere else on the skill page a tag change saves the
 * instant it is made — so the editor *stages* them and writes them with the
 * Save that accepts the rest of the proposal. They ride along here rather than
 * being dropped because the approval card previews the tag changes it
 * proposes, and a card that promises a retag the editor never performs is
 * worse than one that can't offer it.
 *
 * `name` and `accessibility` are still excluded: an update can change neither.
 *
 * Naming the projection keeps the state, the `SkillEditor` prop, and its
 * seeding function from drifting apart.
 */
export type SkillContentSeed = Pick<
  SkillDraft,
  "description" | "body" | "tags" | "removeTags"
>;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A stored scalar, trimmed, or `undefined` when it isn't a string or carries
 * no content.
 *
 * Blank means absent, matching `coerceToSkillDraft` on the write side. The
 * distinction is load-bearing rather than cosmetic: consumers overlay a draft
 * onto already-saved values with `??`, which treats `""` as a value and would
 * blank a populated field. Enforcing the rule here — at the trust boundary,
 * where a tampered or stale-schema payload arrives — keeps every overlay site
 * from having to re-check it.
 */
function asString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * Coerce arbitrary parsed JSON into a {@link SkillDraft}.
 *
 * Returns `null` if the value isn't a plain object. Otherwise returns a
 * draft that contains *only* fields whose runtime shape matches the type —
 * wrong-typed and blank scalars are dropped, a non-array or partially
 * non-string `tags` collapses to the string entries it does carry. A
 * partially-corrupt stored draft yields a partially-populated form rather
 * than a crash.
 *
 * Exposed for tests; callers should use {@link consumeSkillDraft}.
 */
export function parseSkillDraftShape(value: unknown): SkillDraft | null {
  if (!isPlainObject(value)) {
    return null;
  }
  const draft: SkillDraft = {};
  const name = asString(value.name);
  if (name !== undefined) {
    draft.name = name;
  }
  const description = asString(value.description);
  if (description !== undefined) {
    draft.description = description;
  }
  const body = asString(value.body);
  if (body !== undefined) {
    draft.body = body;
  }
  const targetSkillName = asString(value.targetSkillName);
  if (targetSkillName !== undefined) {
    draft.targetSkillName = targetSkillName;
  }
  // Only a recognized tier survives — an unknown / wrong-typed accessibility
  // is dropped so the editor falls back to its `private` default rather than
  // rendering a Select with an out-of-range value.
  if (ACCESSIBILITIES.includes(value.accessibility as SkillAccessibility)) {
    draft.accessibility = value.accessibility as SkillAccessibility;
  }
  const tags = storedTagList(value.tags);
  if (tags !== undefined) {
    draft.tags = tags;
  }
  const removeTags = storedTagList(value.removeTags);
  if (removeTags !== undefined) {
    draft.removeTags = removeTags;
  }
  return draft;
}

/**
 * The string entries of a stored tag list, or `undefined` when it isn't a
 * non-empty array of them.
 *
 * Shared by `tags` and `removeTags` so the two can't drift: `removeTags`
 * drives deletions, which is the one direction abandoning the proposal can't
 * take back, so it gets no weaker a check than the additive one.
 */
function storedTagList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const tags = value.filter((tag): tag is string => typeof tag === "string");
  return tags.length > 0 ? tags : undefined;
}

// ---------------------------------------------------------------------------
// Consume cache
//
// React 18 StrictMode (dev) mounts → unmounts → remounts every component on
// initial render. The consume side effect ("read sessionStorage then remove
// the slot") is not idempotent across that cycle: the first mount reads the
// draft and clears the slot; the synthetic remount would then see an empty
// slot and return `null`. The cache below preserves the consume result across
// one short window (current task + queued macrotask) so the synthetic remount
// returns the same draft. A later true visit (navigate away and back) sees the
// cache cleared and reads from sessionStorage normally. `stashSkillDraft` also
// invalidates the cache so a new write supersedes an older cached read. This
// mirrors the agent-draft handoff — see `shared/domain/agents/agentDraft.ts`.
// ---------------------------------------------------------------------------

interface ConsumeCache {
  value: SkillDraft | null;
}

let consumeCache: ConsumeCache | undefined = undefined;
let consumeCacheClearScheduled = false;

function scheduleConsumeCacheClear(): void {
  if (consumeCacheClearScheduled) {
    return;
  }
  consumeCacheClearScheduled = true;
  // setTimeout(0) fires after the current task and any microtasks it queues.
  // React's StrictMode synthetic remount completes within that same task, so
  // the second mount sees the cache; visits that happen later start a new task
  // and find the cache cleared.
  setTimeout(() => {
    consumeCache = undefined;
    consumeCacheClearScheduled = false;
  }, 0);
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

/**
 * Write the draft to sessionStorage. Silently no-ops if storage is
 * unavailable (e.g. private-mode quirks); the Skills page handles the
 * resulting absent draft as if the user had opened the page directly.
 */
export function stashSkillDraft(draft: SkillDraft): void {
  try {
    sessionStorage.setItem(SKILL_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Storage unavailable — fall back to the empty create form.
  }
  // Invalidate any cached read so the next `consumeSkillDraft` sees this
  // stash, not the prior visit's value.
  consumeCache = undefined;
}

/**
 * Read, validate, and clear the pending draft.
 *
 * Returns `null` when no draft is pending, when the stored payload fails to
 * parse, or when the parsed value isn't a plain object. Any nested field
 * whose runtime shape doesn't match its declared type is dropped silently via
 * {@link parseSkillDraftShape}.
 *
 * The slot is cleared even on parse failure so a corrupt entry can't wedge
 * subsequent visits. The result is briefly cached at the module level — see
 * the comment block above — so a React 18 StrictMode synthetic remount sees
 * the same draft as the first mount rather than an empty slot.
 */
export function consumeSkillDraft(): SkillDraft | null {
  if (consumeCache !== undefined) {
    return consumeCache.value;
  }
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(SKILL_DRAFT_STORAGE_KEY);
  } catch {
    return null;
  }
  let parsed: SkillDraft | null;
  if (raw === null) {
    parsed = null;
  } else {
    try {
      sessionStorage.removeItem(SKILL_DRAFT_STORAGE_KEY);
    } catch {
      // Best-effort clear.
    }
    try {
      parsed = parseSkillDraftShape(JSON.parse(raw));
    } catch {
      parsed = null;
    }
  }
  consumeCache = { value: parsed };
  scheduleConsumeCacheClear();
  return parsed;
}

/**
 * Internal hook for tests — exported so tests can reset state between cases
 * without poking at the storage key directly.
 */
export const __SKILL_DRAFT_STORAGE_KEY_FOR_TESTS: string =
  SKILL_DRAFT_STORAGE_KEY;

/**
 * Internal hook for tests — synchronously clear the module-level consume cache
 * so each test starts from a clean state. Tests normally let the
 * setTimeout-based clear handle this, but a tight sequence of stash+consume
 * cycles in one test needs an explicit reset to avoid the previous test's
 * cache bleeding through.
 */
export function __resetSkillDraftCacheForTests(): void {
  consumeCache = undefined;
  consumeCacheClearScheduled = false;
}
